import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class AppShell extends LitElement {
  static properties = {
    currentPage: { type: String },
    sidebarCollapsed: { type: Boolean },
    connected: { type: Boolean },
    mobileMenuOpen: { type: Boolean },
    notificationCount: { type: Number },
    activeCalls: { type: Number },
    user: { type: Object },
    _userMenuOpen: { state: true }
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
        height: 100%;
        background: transparent;
        color: var(--color-text-primary);
        font-family: var(--font-body);
        overflow: hidden;
      }

      .app-layout {
        display: flex;
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        background: url('/voice/public/bokeh-bg.jpg') center / cover no-repeat fixed;
        position: relative;
        border: 1px solid #ff990057;
      }

      /* Frosted glass + crystal grain over the background image */
      .app-layout::before {
        content: '';
        position: fixed;
        inset: 0;
        background:
          url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.35' numOctaves='5' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23f)' opacity='0.28'/%3E%3C/svg%3E"),
          rgba(255, 255, 255, 0.25);
        background-size: 512px 512px, auto;
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        pointer-events: none;
        z-index: 0;
        mix-blend-mode: overlay;
      }

      /* ─── Sidebar (Heavy frosted glass with frost texture) ─── */
      .sidebar {
        width: var(--sidebar-width);
        background: rgba(255, 253, 250, 0.4);
        backdrop-filter: blur(20px) saturate(1.4);
        -webkit-backdrop-filter: blur(20px) saturate(1.4);
        border-right: 1px solid rgba(255, 255, 255, 0.5);
        box-shadow:
          2px 0 32px -4px rgba(28, 25, 23, 0.06),
          inset -1px 0 0 rgba(255, 255, 255, 0.35);
        display: flex;
        flex-direction: column;
        position: fixed;
        left: 16px;
        top: 16px;
        bottom: 16px;
        border-radius: 24px 0 0 24px;
        z-index: var(--z-fixed);
        transition: width var(--transition-slow);
        overflow: hidden;
      }

      /* Frost texture on sidebar */
      .sidebar::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.35' numOctaves='5' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23f)' opacity='0.15'/%3E%3C/svg%3E");
        background-size: 512px 512px;
        pointer-events: none;
        border-radius: inherit;
        mix-blend-mode: overlay;
        z-index: 0;
      }

      .sidebar.collapsed {
        width: var(--sidebar-collapsed-width);
      }

      /* Logo */
      .sidebar-logo {
        padding: var(--space-6) var(--space-5);
        display: flex;
        align-items: center;
        gap: var(--space-3);
        flex-shrink: 0;
        cursor: pointer;
        margin-bottom: var(--space-2);
      }

      .logo-mark {
        width: 36px;
        height: 36px;
        background: var(--gradient-brand-hero);
        border-radius: var(--radius-lg);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 15px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(45, 143, 111, 0.25);
      }

      .logo-text {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.03em;
        white-space: nowrap;
        overflow: hidden;
        transition: opacity var(--transition-base);
      }

      .sidebar.collapsed .logo-text {
        opacity: 0;
        width: 0;
      }

      /* Navigation */
      .sidebar-nav {
        flex: 1;
        padding: var(--space-3);
        overflow-y: auto;
        overflow-x: hidden;
      }

      .nav-section-label {
        padding: var(--space-4) var(--space-3) var(--space-2);
        font-size: 0.5625rem;
        font-weight: var(--font-bold);
        color: var(--color-accent);
        text-transform: uppercase;
        letter-spacing: 0.15em;
        white-space: nowrap;
        overflow: hidden;
        transition: opacity var(--transition-base);
        position: relative;
      }

      .nav-section-label::after {
        content: '';
        display: block;
        margin-top: var(--space-1);
        height: 1px;
        background: linear-gradient(90deg, rgba(139, 125, 94, 0.25) 0%, transparent 80%);
      }

      .sidebar.collapsed .nav-section-label {
        opacity: 0;
        height: var(--space-4);
        padding: var(--space-2) 0 0 0;
      }

      .nav-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: 0 var(--space-3);
        height: 42px;
        margin-bottom: 2px;
        border-radius: var(--radius-md);
        color: var(--color-text-secondary);
        text-decoration: none;
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition: all var(--transition-fast);
        white-space: nowrap;
        overflow: hidden;
        position: relative;
      }

      .nav-item:hover {
        background: rgba(0, 0, 0, 0.04);
        color: var(--color-text-primary);
      }

      .nav-item.active {
        background: linear-gradient(135deg, rgba(139, 125, 94, 0.1) 0%, rgba(139, 125, 94, 0.05) 100%);
        color: #5C5240;
        font-weight: var(--font-semibold);
        box-shadow: inset 0 0 0 1px rgba(139, 125, 94, 0.12);
      }

      .nav-item-icon {
        width: 20px;
        text-align: center;
        font-size: 15px;
        flex-shrink: 0;
        transition: font-weight var(--transition-fast);
      }

      .nav-item-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: opacity var(--transition-base);
      }

      .sidebar.collapsed .nav-item-text {
        opacity: 0;
        width: 0;
      }

      .nav-item-badge {
        background: rgba(45, 143, 111, 0.1);
        color: #2D8F6F;
        font-size: 10px;
        font-weight: var(--font-bold);
        font-family: var(--font-mono);
        padding: 2px 7px;
        border-radius: var(--radius-full);
        min-width: 20px;
        text-align: center;
        line-height: 1.4;
        flex-shrink: 0;
        transition: all var(--transition-base);
        border: 1px solid rgba(45, 143, 111, 0.15);
        letter-spacing: 0.02em;
      }

      .nav-item.active .nav-item-badge {
        background: #2D8F6F;
        color: white;
        border-color: transparent;
      }

      .sidebar.collapsed .nav-item-badge {
        position: absolute;
        top: 4px;
        right: 4px;
        min-width: 14px;
        padding: 0 4px;
        font-size: 9px;
        background: #2D8F6F;
        color: white;
        border-color: transparent;
      }

      /* Collapse Toggle */
      .sidebar-footer {
        padding: var(--space-3);
        border-top: 1px solid var(--color-border-primary);
        flex-shrink: 0;
      }

      .collapse-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 36px;
        border: none;
        background: rgba(0, 0, 0, 0.03);
        border-radius: var(--radius-md);
        color: var(--color-text-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: 14px;
      }

      .collapse-btn:hover {
        background: rgba(0, 0, 0, 0.06);
        color: var(--color-text-secondary);
      }

      .connection-indicator {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        margin-bottom: var(--space-2);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        overflow: hidden;
        white-space: nowrap;
      }

      .sidebar.collapsed .connection-text {
        opacity: 0;
        width: 0;
      }

      /* ─── Main Content (transparent — cards frost directly over bokeh) ─── */
      .main-area {
        flex: 1;
        margin-left: var(--sidebar-width);
        transition: margin-left var(--transition-slow);
        display: flex;
        flex-direction: column;
        min-height: 100%;
        position: relative;
        background: transparent;
      }

      .sidebar.collapsed ~ .main-area {
        margin-left: var(--sidebar-collapsed-width);
      }

      /* ─── Top Bar (Hidden — replaced by floating cluster) ─── */
      .topbar {
        display: none;
      }

      /* ─── Floating Top-Right Cluster ─── */
      .top-right-cluster {
        position: fixed;
        top: 36px;
        right: 40px;
        z-index: var(--z-sticky);
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .mobile-menu-btn {
        display: none;
        width: 36px;
        height: 36px;
        border: none;
        background: none;
        color: var(--color-text-secondary);
        cursor: pointer;
        border-radius: var(--radius-md);
        font-size: 16px;
      }

      /* Search */
      .search-wrapper {
        position: relative;
      }

      .search-input {
        width: 220px;
        padding: var(--space-2) var(--space-4) var(--space-2) 36px;
        background: rgba(255, 255, 255, 0.8);
        border: 1px solid transparent;
        border-radius: var(--radius-full);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        transition: all var(--transition-base);
        backdrop-filter: blur(8px);
      }

      .search-input:focus {
        outline: none;
        border-color: var(--color-accent);
        box-shadow: var(--shadow-glow-gold);
        background: var(--color-bg-secondary);
        width: 300px;
      }

      .search-input::placeholder {
        color: var(--color-text-tertiary);
      }

      .search-icon {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-text-tertiary);
        font-size: 13px;
        pointer-events: none;
      }

      .search-shortcut {
        position: absolute;
        right: var(--space-2);
        top: 50%;
        transform: translateY(-50%);
        font-family: var(--font-mono);
        font-size: 10px;
        color: var(--color-text-tertiary);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        padding: 1px 5px;
        border-radius: 4px;
        pointer-events: none;
      }

      /* Notification Bell */
      .notification-btn {
        position: relative;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid transparent;
        background: rgba(255, 255, 255, 0.8);
        border-radius: var(--radius-full);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: 14px;
        backdrop-filter: blur(8px);
      }

      .notification-btn:hover {
        background: var(--color-bg-secondary);
        color: var(--color-text-primary);
        box-shadow: var(--shadow-sm);
      }

      .notification-badge {
        position: absolute;
        top: -3px;
        right: -3px;
        background: var(--color-error);
        color: white;
        font-size: 9px;
        font-weight: var(--font-bold);
        min-width: 16px;
        height: 16px;
        border-radius: var(--radius-full);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
        border: 2px solid var(--color-bg-primary);
      }

      /* User Menu */
      .user-menu {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: 4px;
        border: 1px solid transparent;
        border-radius: var(--radius-full);
        cursor: pointer;
        transition: all var(--transition-fast);
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(8px);
      }

      .user-menu:hover {
        background: var(--color-bg-secondary);
        box-shadow: var(--shadow-sm);
      }

      .user-menu.open {
        background: var(--color-bg-secondary);
        box-shadow: var(--shadow-sm);
        border-color: var(--color-border-primary);
      }

      /* User Dropdown */
      .user-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        width: 220px;
        background: var(--color-bg-elevated);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg);
        padding: var(--space-2);
        z-index: var(--z-dropdown);
        animation: dropIn 0.15s var(--ease-out) both;
      }

      @keyframes dropIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .dropdown-header {
        padding: var(--space-3) var(--space-3) var(--space-2);
        border-bottom: 1px solid var(--color-border-subtle);
        margin-bottom: var(--space-1);
      }

      .dropdown-name {
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }

      .dropdown-email {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        margin-top: 2px;
      }

      .dropdown-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-md);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        border: none;
        background: none;
        width: 100%;
        font-family: var(--font-body);
        text-align: left;
      }

      .dropdown-item:hover {
        background: var(--color-bg-hover);
        color: var(--color-text-primary);
      }

      .dropdown-item.logout {
        color: var(--color-error);
      }

      .dropdown-item.logout:hover {
        background: var(--color-error-light);
        color: var(--color-error-text);
      }

      .dropdown-item i {
        width: 16px;
        text-align: center;
        font-size: 13px;
      }

      .user-avatar {
        width: 28px;
        height: 28px;
        background: var(--gradient-brand-hero);
        border-radius: var(--radius-full);
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: var(--font-display);
        font-weight: var(--font-bold);
        font-size: 11px;
        color: white;
      }

      .user-chevron {
        font-size: 10px;
        color: var(--color-text-tertiary);
        padding-right: var(--space-2);
      }

      /* Content Area */
      .content-area {
        flex: 1;
        padding: var(--space-10) var(--space-10) var(--space-8);
        max-width: var(--content-max-width);
        width: 100%;
        margin: 0 auto;
      }

      /* ─── Responsive ─── */
      @media (max-width: 1024px) {
        .sidebar {
          left: 0;
          top: 0;
          bottom: 0;
          border-radius: 0;
          transform: translateX(-100%);
        }

        .sidebar.mobile-open {
          transform: translateX(0);
          box-shadow: var(--shadow-xl);
        }

        .main-area {
          margin-left: 0 !important;
        }

        .mobile-menu-btn {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .top-right-cluster {
          top: 24px;
          right: 24px;
        }

        .search-input {
          width: 180px;
        }

        .search-input:focus {
          width: 220px;
        }

        .search-shortcut {
          display: none;
        }
      }

      @media (max-width: 640px) {
        .content-area {
          padding: var(--space-5);
        }

        .search-wrapper {
          display: none;
        }

        .top-right-cluster {
          top: 20px;
          right: 20px;
        }
      }

      /* Mobile overlay */
      .mobile-overlay {
        display: none;
        position: fixed;
        inset: 0;
        background: rgba(28, 25, 23, 0.3);
        z-index: calc(var(--z-fixed) - 1);
        backdrop-filter: blur(2px);
      }

      .mobile-overlay.visible {
        display: block;
      }
    `
  ];

  constructor() {
    super();
    this.currentPage = 'dashboard';
    this.sidebarCollapsed = false;
    this.connected = false;
    this.mobileMenuOpen = false;
    this.notificationCount = 2;
    this.activeCalls = 0;
    this.user = null;
    this._userMenuOpen = false;
  }

  navigateTo(page) {
    this.currentPage = page;
    this.mobileMenuOpen = false;
    this.dispatchEvent(new CustomEvent('navigate', {
      detail: { page },
      bubbles: true,
      composed: true
    }));
  }

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  get navSections() {
    return [
      {
        label: 'Main',
        items: [
          { id: 'dashboard', icon: 'fa-grid-2', label: 'Dashboard' },
          { id: 'live-calls', icon: 'fa-phone-volume', label: 'Live Calls', badge: this.activeCalls || null },
          { id: 'history', icon: 'fa-clock-rotate-left', label: 'Call History' },
        ]
      },
      {
        label: 'Manage',
        items: [
          { id: 'agents', icon: 'fa-robot', label: 'Agents' },
          { id: 'phone-numbers', icon: 'fa-hashtag', label: 'Phone Numbers' },
          { id: 'knowledge-base', icon: 'fa-book-open', label: 'Knowledge Base' },
          { id: 'campaigns', icon: 'fa-megaphone', label: 'Campaigns' },
        ]
      },
      {
        label: 'Insights',
        items: [
          { id: 'analytics', icon: 'fa-chart-mixed', label: 'Analytics' },
          { id: 'contacts', icon: 'fa-address-book', label: 'Contacts' },
        ]
      },
      {
        label: 'System',
        items: [
          { id: 'settings', icon: 'fa-gear', label: 'Settings' },
        ]
      }
    ];
  }

  getPageTitle() {
    const titles = {
      'dashboard': 'Dashboard',
      'live-calls': 'Live Calls',
      'history': 'Call History',
      'analytics': 'Analytics',
      'agents': 'Agents',
      'settings': 'Settings',
      'phone-numbers': 'Phone Numbers',
      'knowledge-base': 'Knowledge Base',
      'campaigns': 'Campaigns',
      'contacts': 'Contacts'
    };
    return titles[this.currentPage] || 'Dashboard';
  }

  _getUserInitials() {
    if (!this.user) return '?';
    const name = this.user.display_name || this.user.login || this.user.email || '';
    if (!name) return '?';
    const parts = name.split(/[\s.]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  }

  _getDisplayName() {
    if (!this.user) return '';
    return this.user.display_name || this.user.login || this.user.email;
  }

  _toggleUserMenu(e) {
    e.stopPropagation();
    this._userMenuOpen = !this._userMenuOpen;
    if (this._userMenuOpen) {
      // Close on outside click
      const close = () => { this._userMenuOpen = false; document.removeEventListener('click', close); };
      setTimeout(() => document.addEventListener('click', close), 0);
    }
  }

  _handleLogout() {
    this._userMenuOpen = false;
    this.dispatchEvent(new CustomEvent('logout', { bubbles: true, composed: true }));
  }

  renderNavItem(item) {
    const isActive = this.currentPage === item.id;
    const iconPrefix = isActive ? 'fa-sharp fa-solid' : 'fa-sharp fa-light';
    return html`
      <div
        class="nav-item ${isActive ? 'active' : ''}"
        @click="${() => this.navigateTo(item.id)}"
        title="${this.sidebarCollapsed ? item.label : ''}"
      >
        <i class="${iconPrefix} ${item.icon} nav-item-icon"></i>
        <span class="nav-item-text">${item.label}</span>
        ${item.badge ? html`<span class="nav-item-badge">${item.badge}</span>` : ''}
      </div>
    `;
  }

  render() {
    return html`
      ${iconStyles}
      <!-- Mobile Overlay -->
      <div
        class="mobile-overlay ${this.mobileMenuOpen ? 'visible' : ''}"
        @click="${() => this.mobileMenuOpen = false}"
      ></div>

      <div class="app-layout">
        <!-- Sidebar -->
        <aside class="sidebar ${this.sidebarCollapsed ? 'collapsed' : ''} ${this.mobileMenuOpen ? 'mobile-open' : ''}">
          <!-- Logo -->
          <div class="sidebar-logo" @click="${() => this.navigateTo('dashboard')}">
            <div class="logo-mark">
              <i class="fa-sharp fa-solid fa-waveform-lines"></i>
            </div>
            <span class="logo-text">Voice AI</span>
          </div>

          <!-- Navigation -->
          <nav class="sidebar-nav">
            ${this.navSections.map(section => html`
              <div class="nav-section-label">${section.label}</div>
              ${section.items.map(item => this.renderNavItem(item))}
            `)}
          </nav>

          <!-- Footer -->
          <div class="sidebar-footer">
            <div class="connection-indicator">
              <div class="status-dot ${this.connected ? 'active' : 'inactive'}"></div>
              <span class="connection-text">${this.connected ? 'Connected' : 'Offline'}</span>
            </div>
            <button class="collapse-btn" @click="${this.toggleSidebar}" title="${this.sidebarCollapsed ? 'Expand' : 'Collapse'}">
              <i class="fa-sharp fa-light ${this.sidebarCollapsed ? 'fa-angles-right' : 'fa-angles-left'}"></i>
            </button>
          </div>
        </aside>

        <!-- Main Content -->
        <div class="main-area">
          <!-- Floating Top-Right Cluster -->
          <div class="top-right-cluster">
            <button class="mobile-menu-btn" @click="${this.toggleMobileMenu}">
              <i class="fa-sharp fa-light fa-bars"></i>
            </button>

            <div class="search-wrapper">
              <i class="fa-sharp fa-light fa-magnifying-glass search-icon"></i>
              <input
                type="text"
                class="search-input"
                placeholder="Search..."
              />
              <span class="search-shortcut">⌘K</span>
            </div>

            <button class="notification-btn" title="Notifications">
              <i class="fa-sharp fa-light fa-bell"></i>
              ${this.notificationCount > 0 ? html`
                <span class="notification-badge">${this.notificationCount}</span>
              ` : ''}
            </button>

            <div class="user-menu ${this._userMenuOpen ? 'open' : ''}" @click="${this._toggleUserMenu}" style="position:relative">
              <div class="user-avatar">${this._getUserInitials()}</div>
              <i class="fa-sharp fa-light fa-chevron-down user-chevron"></i>
              ${this._userMenuOpen ? html`
                <div class="user-dropdown" @click="${e => e.stopPropagation()}">
                  <div class="dropdown-header">
                    <div class="dropdown-name">${this._getDisplayName()}</div>
                    <div class="dropdown-email">${this.user?.email || ''}</div>
                  </div>
                  <button class="dropdown-item logout" @click="${this._handleLogout}">
                    <i class="fa-sharp fa-light fa-arrow-right-from-bracket"></i>
                    Sign out
                  </button>
                </div>
              ` : ''}
            </div>
          </div>

          <!-- Page Content -->
          <main class="content-area">
            <slot></slot>
          </main>
        </div>
      </div>
    `;
  }
}

customElements.define('app-shell', AppShell);
